import { useEffect, useId, useState } from "react";
import { canDocflow } from "@/auth/portal";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CircleCheck,
  Download,
  Info,
  Loader2,
  Pencil,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

import {
  checkPartnerRegistry,
  editPartnerRequisitesFn,
  loadPartnerCard,
  loadPartnerFile,
  reviewPartnerFn,
  setTaxMarkupFn,
  loadPdLog,
} from "@/braining/partners.functions";
import { registryChecks } from "@/braining/registry";
import { crossChecks, type CheckLevel } from "@/braining/partner-docs";
import type { PartnerCard } from "@/braining/partners-store";
import { KIND_LABEL, STATUS_LABEL } from "@/braining/partners";
import {
  DOC_FLOWS,
  DOCS,
  FORM,
  completeness,
  problemsOf,
  shown,
  suggestedMarkup,
  type Problems,
  type Values,
} from "@/braining/partner-form";
import { RequisitesField } from "@/braining/components/requisites-field";
import { NewDeal } from "@/braining/components/new-deal";
import { loadDeals } from "@/braining/deals.functions";
import { DEAL_STATUS_LABEL } from "@/braining/deals";
import type { Deal } from "@/braining/deals-store";
import { REQ_STATE_LABEL, reviewKey } from "@/braining/partner-requisites";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Карточка подрядчика у Даши: что он заполнил, что не сходится, принять
 * или вернуть с замечанием. Не справился сам — Даша вписывает реквизиты
 * за него («Изменить реквизиты»), и карточка помнит, кто вписал.
 *
 * Проверки те же, что видит подрядчик при заполнении, — контрольные числа
 * ИНН, ОГРН, счёта по БИК. Сверка с реестрами и между документами
 * появится на этапах 2б–2в; до тех пор то, что машина проверить не может,
 * Даша смотрит глазами.
 */
export const Route = createFileRoute("/contractor/$id")({
  head: () => ({ meta: [{ title: "Подрядчик — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  loader: ({ params }) => loadPartnerCard({ data: { id: params.id } }),
  component: ContractorPage,
});

function ContractorPage() {
  const initial = Route.useLoaderData();
  if (!initial) {
    return <p className="text-sm text-muted-foreground">Подрядчик не найден.</p>;
  }
  return <CardView initial={initial} />;
}

function CardView({ initial }: { initial: PartnerCard }) {
  const [card, setCard] = useState(initial);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"approve" | "return" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const req = card.requisites;
  const problems = problemsOf(card.kind, req.values, { forSubmit: true });
  const done = completeness(card.kind, req.values);
  const canApprove = req.state === "submitted";
  const canReturn = req.state === "submitted" || req.state === "approved";

  async function decide(decision: "approve" | "return") {
    setBusy(decision);
    setError(null);
    try {
      // Отпечаток того, что на экране: сервер примет решение, только если
      // подрядчик ничего не поменял с момента, как карточка открылась.
      const res = await reviewPartnerFn({
        data: { id: card.id, decision, note, seen: reviewKey(card.requisites) },
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setCard(res.next);
      setNote("");
      toast.success(
        decision === "approve" ? "Реквизиты приняты" : "Вернули подрядчику с замечанием",
      );
    } catch (e) {
      console.error("[contractor] не удалось сохранить решение", e);
      setError("Не удалось сохранить решение");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <Link
          to="/contractors"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Подрядчики
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">{card.name}</h1>
          <Badge variant="outline" className="font-normal">
            {KIND_LABEL[card.kind]}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {[card.email, STATUS_LABEL[card.status]].filter(Boolean).join(" · ")}
        </p>
        <TaxMarkupField card={card} onCard={setCard} />
        <OkvedLine card={card} />
      </div>

      <Card className={cn(req.state === "submitted" && "border-primary/40")}>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              Реквизиты: {REQ_STATE_LABEL[req.state].toLowerCase()}
            </span>
            <span className="text-sm text-muted-foreground">
              заполнено {done.filled} из {done.total}
            </span>
          </div>
          {req.consent && (
            <p className="text-xs text-muted-foreground">
              Согласие на обработку данных дано {fmt(req.consent.at)} (редакция{" "}
              {req.consent.version}).
            </p>
          )}
          {req.enteredBy && (
            <p className="text-xs text-muted-foreground">
              Последнюю правку внёс сотрудник: {req.enteredBy.by}, {fmt(req.enteredBy.at)}.
            </p>
          )}
          {req.review && (
            <p className="text-xs text-muted-foreground">
              {req.state === "returned" ? "Вернул" : "Проверил"} {req.review.by},{" "}
              {fmt(req.review.at)}
              {req.review.note && ` — «${req.review.note}»`}
            </p>
          )}

          {(canApprove || canReturn) && (
            <div className="space-y-2 border-t pt-3">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Замечание подрядчику — обязательно, если возвращаете"
                rows={2}
              />
              <div className="flex flex-wrap gap-2">
                {canApprove && (
                  <Button onClick={() => void decide("approve")} disabled={busy !== null}>
                    {busy === "approve" ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Check className="size-4" />
                    )}
                    Принять
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => void decide("return")}
                  disabled={busy !== null || !note.trim()}
                >
                  {busy === "return" ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Undo2 className="size-4" />
                  )}
                  Вернуть с замечанием
                </Button>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-destructive-ink">{error}</p>}
        </CardContent>
      </Card>

      <DealsOf card={card} />

      <DocsReview card={card} onCard={setCard} />

      <RequisitesSections card={card} onCard={setCard} />

      <PdLog id={card.id} />
    </div>
  );
}

/**
 * Кто из сотрудников открывал карточку, скачивал документы, вписывал или
 * принимал реквизиты (152-ФЗ). Видят администратор и руководитель.
 */
function PdLog({ id }: { id: string }) {
  const { user } = Route.useRouteContext();
  const lead = user?.role === "admin" || user?.role === "rop";
  const [rows, setRows] = useState<{ at: string; who: string; action: string }[] | null>(null);
  if (!lead) return null;
  return (
    <details
      className="rounded-xl border bg-card px-5 py-3 text-sm"
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open && !rows) {
          loadPdLog({ data: { id } })
            .then(setRows)
            .catch((err: unknown) => console.error("[contractor] журнал не загрузился", err));
        }
      }}
    >
      <summary className="cursor-pointer font-medium">Кто смотрел данные</summary>
      <div className="mt-2 space-y-1 text-muted-foreground">
        {rows === null ? (
          <p>Загружаем…</p>
        ) : rows.length === 0 ? (
          <p>Записей нет.</p>
        ) : (
          rows.map((r, i) => (
            <p key={i}>
              {fmt(r.at)} · {r.who} · {r.action}
            </p>
          ))
        )}
      </div>
    </details>
  );
}

/**
 * Реквизиты: просмотр с отметками проверок или, по кнопке, та же анкета,
 * что у подрядчика, — для того, кто сам не заполнит.
 */
function RequisitesSections({
  card,
  onCard,
}: {
  card: PartnerCard;
  onCard: (c: PartnerCard) => void;
}) {
  const uid = useId();
  const req = card.requisites;
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Values>(req.values);
  const [fieldProblems, setFieldProblems] = useState<Problems>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const problems = problemsOf(card.kind, req.values, { forSubmit: true });

  function start() {
    setValues({
      ...req.values,
      email: req.values.email || card.email || "",
      docFlow: req.values.docFlow || (card.kind === "self_employed" ? DOC_FLOWS[1] : ""),
    });
    setFieldProblems({});
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    try {
      const res = await editPartnerRequisitesFn({
        data: { id: card.id, values, seen: reviewKey(req) },
      });
      if (!res.ok) {
        setError(res.error);
        setFieldProblems(res.problems ?? {});
        return;
      }
      onCard(res.next);
      setEditing(false);
      toast.success(
        res.next.requisites.state === "submitted"
          ? "Сохранено — анкета полная, можно принимать"
          : "Сохранено",
      );
    } catch (e) {
      console.error("[contractor] реквизиты не сохранились", e);
      setError("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <>
        {FORM[card.kind].map((section) => (
          <Card key={section.title}>
            <CardContent className="space-y-4 p-5">
              <h2 className="font-medium">{section.title}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {section.fields
                  .filter((f) => shown(f, values))
                  .map((f) => (
                    <RequisitesField
                      key={f.key}
                      id={`${uid}-${f.key}`}
                      spec={f}
                      value={values[f.key] ?? ""}
                      problem={fieldProblems[f.key]}
                      onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                    />
                  ))}
              </div>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="space-y-3 p-5">
            <p className="text-sm text-muted-foreground">
              Полная анкета после сохранения встанет «на проверку» — примете её кнопкой выше. В
              карточке останется отметка, что реквизиты вписали вы.
            </p>
            {error && <p className="text-sm text-destructive-ink">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void save()} disabled={saving}>
                {saving && <Loader2 className="size-4 animate-spin" />}
                Сохранить
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                Отмена
              </Button>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={start}>
          <Pencil className="size-4" />
          Изменить реквизиты
        </Button>
      </div>
      {FORM[card.kind].map((section) => (
        <Card key={section.title}>
          <CardContent className="p-5">
            <h2 className="mb-3 font-medium">{section.title}</h2>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(0,14rem)_1fr]">
              {section.fields
                .filter((f) => shown(f, req.values))
                .map((f) => {
                  const v = req.values[f.key];
                  const p = problems[f.key];
                  return (
                    <div key={f.key} className="contents">
                      <dt className="text-muted-foreground">{f.label}</dt>
                      <dd className={cn("min-w-0 break-words", p && "text-destructive-ink")}>
                        {v || "—"}
                        {p && v ? <span className="block text-xs">{p}</span> : null}
                      </dd>
                    </div>
                  );
                })}
            </dl>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

/**
 * Налог подрядчика «сверху»: договариваются о сумме «на руки», а налог по
 * его ставке закладывают в договор (ответ Даши 21.10). Видит только
 * сотрудник; комплект посчитает из него сумму договора. Если пусто, а в
 * анкете указана ставка УСН, — подсказка её подставить.
 */
function TaxMarkupField({ card, onCard }: { card: PartnerCard; onCard: (c: PartnerCard) => void }) {
  const saved = card.taxMarkup == null ? "" : String(card.taxMarkup);
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  const dirty = value.trim().replace(",", ".") !== saved;
  const hint = suggestedMarkup(card.requisites.values);

  async function save() {
    const raw = value.trim().replace(",", ".");
    const pct = raw === "" ? null : Number(raw);
    if (pct != null && (!Number.isFinite(pct) || pct < 0 || pct > 30)) {
      toast.error("Процент — от 0 до 30");
      return;
    }
    setBusy(true);
    try {
      const next = await setTaxMarkupFn({ data: { id: card.id, pct } });
      if (next) {
        onCard(next);
        setValue(next.taxMarkup == null ? "" : String(next.taxMarkup));
        toast.success("Сохранено");
      }
    } catch (e) {
      console.error("[contractor] надбавка не сохранилась", e);
      toast.error("Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Налог сверху, %</span>
      <Input
        value={value}
        inputMode="decimal"
        placeholder="—"
        className="h-8 w-20"
        onChange={(e) => setValue(e.target.value)}
      />
      {dirty && (
        <Button size="sm" variant="outline" onClick={() => void save()} disabled={busy}>
          {busy && <Loader2 className="size-4 animate-spin" />}
          Сохранить
        </Button>
      )}
      {!value.trim() && hint != null && (
        <Button size="sm" variant="ghost" onClick={() => setValue(String(hint))}>
          По анкете — {hint}%
        </Button>
      )}
      <span className="text-xs text-muted-foreground">видно только сотрудникам</span>
    </div>
  );
}

/**
 * ОКВЭДы — посмотреть быстро (Даша 29.10): из анкеты или выписки ЕГРИП /
 * карточки организации, которую подрядчик загрузил (распознаются сами), и
 * основной — с сайта, из сверки с реестром (DaData даёт только основной).
 */
function OkvedLine({ card }: { card: PartnerCard }) {
  if (card.kind === "self_employed") return null;
  const own = (card.requisites.values.okveds ?? "").trim();
  const party = card.requisites.registry?.party;
  const site = party?.found && party.okved ? party.okved : null;
  if (!own && !site) {
    return (
      <p className="mt-1 text-sm text-muted-foreground">
        ОКВЭД: нет данных — сверьте с реестром или попросите выписку.
      </p>
    );
  }
  return (
    <p className="mt-1 text-sm">
      <span className="text-muted-foreground">ОКВЭД: </span>
      {own || "—"}
      {site && (
        <span className="text-muted-foreground">
          {" "}
          · основной по реестру {site}
          {party?.okvedName ? ` — ${party.okvedName}` : ""}
        </span>
      )}
    </p>
  );
}

/** Комплекты документов этого подрядчика и кнопка завести новый. */
function DealsOf({ card }: { card: PartnerCard }) {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  useEffect(() => {
    loadDeals({ data: { contractorId: card.id } })
      .then(setDeals)
      .catch((e: unknown) => console.error("[contractor] сделки не загрузились", e));
  }, [card.id]);
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center gap-2">
          <h2 className="font-medium">Комплекты документов</h2>
          <span className="text-xs text-muted-foreground">{deals?.length ?? ""}</span>
          <div className="ml-auto">
            <NewDeal
              contractor={{
                id: card.id,
                name: card.name,
                kind: card.kind,
                docFlow: card.requisites.values.docFlow ?? null,
              }}
            />
          </div>
        </div>
        {deals && deals.length > 0 && (
          <div className="divide-y rounded-md border text-sm">
            {deals.map((d) => (
              <Link
                key={d.id}
                to="/deal/$id"
                params={{ id: d.id }}
                className="flex flex-wrap items-center gap-x-3 px-3 py-2 hover:bg-muted/50"
              >
                <span className="min-w-0 flex-1 truncate">
                  {d.title}
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    · {d.project || "без проекта"}
                  </span>
                </span>
                <span className="tabular-nums">
                  {d.amountTotal == null
                    ? "—"
                    : `${d.amountTotal.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`}
                </span>
                <span className="w-44 text-right text-xs text-muted-foreground">
                  {DEAL_STATUS_LABEL[d.status]}
                </span>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
}

const LEVEL_ICON: Record<CheckLevel, typeof Info> = {
  ok: CircleCheck,
  warn: Info,
  bad: AlertTriangle,
};

const LEVEL_TONE: Record<CheckLevel, string> = {
  ok: "text-muted-foreground",
  warn: "text-warning-ink",
  bad: "text-destructive-ink",
};

/**
 * Документы комплекта и сверка — то, что Даша сейчас делает глазами. Сначала
 * расхождения, потом предупреждения, потом что сошлось: видно и что не так,
 * и что машина уже проверила за неё.
 */
function DocsReview({ card, onCard }: { card: PartnerCard; onCard: (c: PartnerCard) => void }) {
  const recognized = card.requisites.recognized ?? {};
  const registry = card.requisites.registry;
  const order: CheckLevel[] = ["bad", "warn", "ok"];
  const checks = [
    ...crossChecks(card.kind, card.requisites.values, recognized),
    ...registryChecks(card.kind, card.requisites.values, registry),
  ].sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level));
  const [busy, setBusy] = useState<string | null>(null);
  const inn = card.requisites.values.inn ?? "";

  async function recheck() {
    setBusy("registry");
    try {
      const res = await checkPartnerRegistry({ data: { id: card.id } });
      if (res.ok) {
        onCard(res.next);
        toast.success(
          card.kind === "self_employed"
            ? "Поставили в очередь ФНС — ответ появится здесь"
            : "Сверили с реестром",
        );
      } else toast.error(res.error);
    } catch (e) {
      console.error("[contractor] сверка с реестром не прошла", e);
      toast.error("Сверка не прошла");
    } finally {
      setBusy(null);
    }
  }

  /**
   * Запасной путь проверки самозанятого: ИНН в буфер и сайт ФНС в новой
   * вкладке — когда API ФНС лежит, а ответ нужен сейчас.
   */
  async function openFns() {
    try {
      await navigator.clipboard.writeText(inn);
      toast.success("ИНН скопирован — вставьте его на сайте ФНС");
    } catch {
      // буфер недоступен — ИНН виден рядом с кнопкой
    }
    window.open("https://npd.nalog.ru/check-status/", "_blank", "noopener,noreferrer");
  }

  async function download(docKind: string) {
    setBusy(docKind);
    try {
      const f = await loadPartnerFile({ data: { id: card.id, docKind } });
      if (!f) {
        toast.error("Файл не найден");
        return;
      }
      const bytes = Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: f.mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      console.error("[contractor] не удалось скачать документ", e);
      toast.error("Не удалось скачать документ");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <h2 className="font-medium">Документы и сверка</h2>
        <div className="divide-y rounded-md border text-sm">
          {DOCS[card.kind].map((spec) => {
            const d = recognized[spec.key];
            return (
              <div key={spec.key} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className={cn(!d && "text-muted-foreground")}>{spec.label}</span>
                <span className="text-xs text-muted-foreground">
                  {d
                    ? `${d.fileName} · ${new Date(d.at).toLocaleDateString("ru-RU")}${
                        d.docDate ? ` · документ от ${d.docDate}` : ""
                      }`
                    : spec.required
                      ? "не приложен"
                      : "не приложен (не обязателен)"}
                </span>
                {d &&
                  (d.stored ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      disabled={busy === spec.key}
                      onClick={() => void download(spec.key)}
                    >
                      {busy === spec.key ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Download className="size-4" />
                      )}
                      Скачать
                    </Button>
                  ) : (
                    <span className="ml-auto text-xs text-muted-foreground">
                      скан не храним — только данные
                    </span>
                  ))}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy === "registry" || !inn}
            onClick={() => void recheck()}
          >
            {busy === "registry" && <Loader2 className="size-4 animate-spin" />}
            {card.kind === "self_employed" ? "Проверить в ФНС" : "Сверить с реестром"}
          </Button>
          {/* Запасной путь: сервис ФНС бывает недоступен часами, а ответ
              нужен сейчас. ИНН копируется сам — на сайте его остаётся вставить. */}
          {card.kind === "self_employed" && (
            <Button size="sm" variant="ghost" disabled={!inn} onClick={() => void openFns()}>
              Открыть сайт ФНС
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {!inn
              ? "ИНН ещё не заполнен"
              : registry
                ? `сверено ${new Date(registry.checkedAt).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })}`
                : "ещё не сверяли"}
          </span>
        </div>
        {checks.length > 0 && (
          <ul className="space-y-1 text-sm">
            {checks.map((c) => {
              const Icon = LEVEL_ICON[c.level];
              return (
                <li key={c.text} className={cn("flex gap-2", LEVEL_TONE[c.level])}>
                  <Icon className="mt-0.5 size-4 shrink-0" />
                  {c.text}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
